import { useState } from "react";
import { OptOutConfirmationDialog } from "@/core/components/opt-out-confirmation-dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  Tabs,
  TabsTrigger,
  TabsContent,
  TabsList,
} from "@volca/ui/components/ui/tabs";
import { toast } from "@volca/ui/lib/toast";
import { BusinessSettingsSidebarHeader } from "@/business-settings/common/business-settings-sidebar-header";
import { BusinessSettingsSidebarWrapper } from "@/business-settings/common/business-settings-sidebar-wrapper";
import { ReferralList } from "@/business-settings/common/referral-list";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { NoteList } from "@/business-settings/customers/common/note-list";
import { PayoutList } from "@/business-settings/customers/common/payout-list";
import { DetailsTab } from "@/business-settings/customers/details";
import { getErrorMessage } from "@/core/api/errors";
import { formatPhoneNumber } from "@/core/utils/phone";
import { CUSTOMER_STATE } from "@/customer/constants";
import {
  useGetAdvocate,
  useGetCustomerNotes,
  useOptOutCustomer,
} from "@/customer/query";
import { useGetMyUser } from "@/user/query";
import { isAdminOrOwner } from "@/user/utils";
import { AdvocateSidebarSkeleton } from "./advocate-sidebar-skeleton";

interface AdvocateSidebarProps {
  businessId: string;
  mode: "view" | "new";
  advocateId: string | undefined;
}

const AdvocateSidebar = ({
  businessId,
  mode,
  advocateId,
}: AdvocateSidebarProps) => {
  const { data: advocate = null, isLoading } = useGetAdvocate(advocateId);

  const [activeTab, setActiveTab] = useState("details");
  const { resetSidebar } = useBusinessSettingsSidebar();
  const isNewMode = mode === "new";
  const { data: user } = useGetMyUser();
  const optOutCustomer = useOptOutCustomer(businessId);
  const [isOptOutDialogOpen, setIsOptOutDialogOpen] = useState(false);

  const customerId = advocateId || "";
  const { data: notes = [] } = useGetCustomerNotes(customerId);

  const canChangeAttribution = isAdminOrOwner(user?.role);
  const { referrals = [], payouts = [] } = advocate || {};

  const headerText = "Advocates";
  const name =
    advocate?.name ||
    formatPhoneNumber(advocate?.phone_number) ||
    "New advocate";

  const handleOptOut = () => {
    if (advocateId) {
      optOutCustomer.mutate(advocateId, {
        onSuccess: () => {
          toast.success("Customer opted out of messaging");
          setIsOptOutDialogOpen(false);
          resetSidebar();
        },
        onError: (error) => {
          toast.error("Couldn't opt the customer out of messaging", {
            description: getErrorMessage(error),
          });
        },
      });
    }
  };

  const dropdownActions =
    mode === "view" &&
    advocateId &&
    advocate?.state !== CUSTOMER_STATE.opted_out
      ? [
          {
            label: "Opt out of messaging",
            onClick: () => setIsOptOutDialogOpen(true),
            variant: "destructive" as const,
          },
        ]
      : undefined;

  if (isLoading) {
    return <AdvocateSidebarSkeleton />;
  }

  return (
    <BusinessSettingsSidebarWrapper>
      <BusinessSettingsSidebarHeader
        headerText={headerText}
        name={name}
        onClose={resetSidebar}
        dropdownActions={dropdownActions}
      />
      <OptOutConfirmationDialog
        open={isOptOutDialogOpen}
        onOpenChange={setIsOptOutDialogOpen}
        customerName={name}
        isPending={optOutCustomer.isPending}
        onConfirm={handleOptOut}
      />
      <div className="flex-1 overflow-hidden">
        <Stack align="start" gap={0} className="h-full w-full">
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="flex w-full flex-1 flex-col gap-0 overflow-hidden px-6"
          >
            <div className="w-full px-1">
              <TabsList variant="line" className="w-full justify-center pb-0">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="notes">Notes ({notes.length})</TabsTrigger>
                <TabsTrigger value="referrals">
                  Referrals ({referrals.length})
                </TabsTrigger>
                <TabsTrigger value="payouts">
                  Payouts ({payouts.length})
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent
              value="details"
              className="min-h-0 overflow-y-hidden pt-4"
            >
              <DetailsTab
                businessId={businessId}
                initialData={advocate}
                isNewMode={isNewMode}
                canChangeAttribution={canChangeAttribution}
              />
            </TabsContent>

            <TabsContent value="notes" className="min-h-0 overflow-y-hidden">
              <NoteList notes={notes} customerId={advocateId} />
            </TabsContent>

            <TabsContent
              value="referrals"
              className="min-h-0 overflow-y-hidden"
            >
              <ReferralList
                referrals={referrals}
                isLoading={false}
                hideHeader
              />
            </TabsContent>

            <TabsContent value="payouts" className="min-h-0 overflow-y-hidden">
              <PayoutList payouts={payouts} />
            </TabsContent>
          </Tabs>
        </Stack>
      </div>
    </BusinessSettingsSidebarWrapper>
  );
};

export default AdvocateSidebar;
